'use client';

import { useCallback, useState } from 'react';
import type {
  AuthenticatedUser,
  InboxAccount,
} from '@frameio/graphql-client/generated';
import {
  useActiveAccountId,
  useDispatchAppState,
} from '@frameio/shared/contexts';
import type { AvatarSizes } from '@frameio/vapor';
import { useOnStateChange } from '@frameio/vapor';
import { AccountAvatar } from './AccountAvatar';
import { AVATAR_SIZE } from './constants';
import { useGetAllAccountsForPrimaryNavPanelQuery } from './gql/queries/generated/GetAllAccountsForPrimaryNavPanel';

const AvatarLoading = ({ size = AVATAR_SIZE }: { size?: AvatarSizes }) => (
  <div style={{ height: `${size}px`, width: `${size}px` }} />
);

/**
 * Renders an AccountAvatar using the active account ID (context).
 */
export const ActiveAccountAvatar = ({
  size = AVATAR_SIZE,
  showHalo = true,
  minimal,
}: {
  size?: AvatarSizes;
  showHalo?: boolean;
  minimal?: boolean;
}) => {
  const activeAccountId = useActiveAccountId();

  // Changing the account will navigate to a new route
  // We want to show the new account avatar immediately, optimistically
  const dispatch = useDispatchAppState();
  const [optimisticAccountId, setOptimisticAccountId] = useState<string>();
  const accountId = optimisticAccountId || activeAccountId;

  const meData = useGetAllAccountsForPrimaryNavPanelQuery({
    fetchPolicy: 'cache-and-network',
  });
  const me = meData.data?.me as AuthenticatedUser;
  const account = me?.accounts?.nodes?.find(
    (node) => node.account.id === accountId
  );
  const inboxAccount = me?.inboxAccounts?.nodes?.find(
    (node) => node.id === accountId
  );
  const accountData = account ? account.account : inboxAccount;

  useOnStateChange(activeAccountId, () => {
    setOptimisticAccountId(activeAccountId);
  });

  const handleAccountChanged = useCallback(
    (newAccountId: string) => {
      setOptimisticAccountId(newAccountId);
      dispatch({ type: 'CLOSE_MOBILE_NAV' });
    },
    [dispatch]
  );

  if (!accountData) {
    return <AvatarLoading size={size} />;
  }

  // Hide the account switcher on the migration gate when the user has only one V3 account
  const totalAccounts =
    (me?.accounts?.nodes?.length ?? 0) +
    (me?.inboxAccounts?.nodes?.length ?? 0);
  if (minimal && totalAccounts <= 1 && accountData.version !== 4) {
    return null;
  }

  return (
    <AccountAvatar
      account={accountData as InboxAccount}
      minimal={minimal}
      onAccountChanged={handleAccountChanged}
      showHalo={showHalo}
      size={size}
      skipFadeIn={optimisticAccountId !== undefined}
    />
  );
};
